/**
 * career-theme-settings — the DEV-gated design/theme tuning surface (route
 * `/settings/theme`, docs/05-ux-ui-guidelines.md §6). It lets the operator try
 * CONTROL-color and SUCCESS-color options and watch the WHOLE app recolor instantly,
 * then persist the choice (localStorage `career-tokens`, re-applied on boot).
 *
 * Because the token layers are split (brand ≠ control ≠ semantic), tuning the control
 * or success color NEVER moves the brand mark. The surface tunes the ACTIVE theme
 * (what the app currently shows); switch theme in the user menu to tune the other.
 *
 * Seamless-document idiom (§0): headings + hairlines, no boxed cards as structure.
 */

import {
  KitElement,
  signal,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import {
  CONTROL_PRESETS,
  TUNABLE_TOKENS,
  clearAll,
  clearOverride,
  effectiveTheme,
  exportCss,
  getOverride,
  resolvedHex,
  setOverride,
  setPresetPair,
  toHex6,
  type ThemeName,
  type TunableToken,
} from "../lib/theme-tokens"

const THEME_LABEL: Record<ThemeName, string> = { light: "Hell", dark: "Dunkel" }

class ThemeSettingsVm implements ViewModel {
  /** Bumped after every mutation / theme flip to re-seed the inputs. */
  readonly tick: Signal<number> = signal(0)
  readonly copied: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>

  constructor() {
    this.renderDeps = [this.tick, this.copied]
  }

  bump(): void {
    this.tick.set(this.tick.value + 1)
  }

  attach(): () => void {
    // Re-render when the theme flips so the panel re-seeds against the shown theme.
    let obs: MutationObserver | null = null
    try {
      obs = new MutationObserver(() => this.bump())
      obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] })
    } catch {
      /* observers unavailable */
    }
    return () => obs?.disconnect()
  }

  theme(): ThemeName {
    return effectiveTheme()
  }

  async copyExport(): Promise<void> {
    try {
      await navigator.clipboard?.writeText(exportCss())
      this.copied.set(true)
      setTimeout(() => this.copied.set(false), 1800)
    } catch {
      /* clipboard blocked — the textarea still holds the snippet to copy by hand. */
    }
  }
}

export class CareerThemeSettings extends KitElement<ThemeSettingsVm> {
  protected createViewModel(_ctx: KitContext): ThemeSettingsVm {
    return new ThemeSettingsVm()
  }

  /** One tunable token row: swatch + native color picker + exact hex + reset. */
  private tokenRow(vm: ThemeSettingsVm, t: TunableToken): TemplateResult {
    const theme = vm.theme()
    const current = resolvedHex(t.cssVar)
    const overridden = getOverride(theme, t.cssVar) !== undefined
    return html`
      <div class="tc-token">
        <span class="tc-swatch" style=${`background:var(${t.cssVar})`} aria-hidden="true"></span>
        <span class="tc-token-meta">
          <span class="tc-token-label">${t.label} <code class="tc-var">${t.cssVar}</code></span>
          <span class="tc-token-note">${t.note}</span>
        </span>
        <input
          class="tc-color"
          type="color"
          .value=${current}
          aria-label=${`${t.label} wählen`}
          @input=${(e: Event) => {
            setOverride(theme, t.cssVar, (e.target as HTMLInputElement).value)
            vm.bump()
          }}
        />
        <input
          class="tc-hex"
          type="text"
          spellcheck="false"
          .value=${current}
          aria-label=${`${t.label} Hex`}
          @change=${(e: Event) => {
            setOverride(theme, t.cssVar, toHex6((e.target as HTMLInputElement).value))
            vm.bump()
          }}
        />
        ${overridden
          ? html`<button
              type="button"
              class="tc-reset"
              @click=${() => {
                clearOverride(theme, t.cssVar)
                vm.bump()
              }}
            >
              Zurücksetzen
            </button>`
          : html`<span class="tc-default">Standard</span>`}
      </div>
    `
  }

  protected render(vm: ThemeSettingsVm): TemplateResult {
    void vm.tick.value // subscribe
    const theme = vm.theme()
    const controlToks = TUNABLE_TOKENS.filter((t) => t.layer === "control")
    const navToks = TUNABLE_TOKENS.filter((t) => t.layer === "nav")
    const semanticToks = TUNABLE_TOKENS.filter((t) => t.layer === "semantic")
    const snippet = exportCss()

    return html`
      <section class="surface tc" aria-labelledby="career-surface-title">
        <h1 id="career-surface-title" class="surface-title">Design &amp; Theme</h1>
        <p class="surface-lead">
          Drei getrennte Farbebenen: <strong>Marke</strong> (der P-Mark, an den »personal«-Akzent
          #DE8D51 gekoppelt — hier bewusst nicht änderbar), <strong>Steuerung</strong> (Buttons,
          Links, Fokus) und <strong>Semantik</strong> (Erfolg, Warnung, Kritisch). Änderungen unten
          gelten für das gerade angezeigte Theme (<strong>${THEME_LABEL[theme]}</strong>) und färben
          die App sofort um — die Marke bleibt unberührt.
        </p>
        <p class="tc-hint">
          Das jeweils andere Theme stimmst du ab, indem du oben rechts im Menü auf
          <em>${theme === "light" ? "Dunkel" : "Hell"}</em> umschaltest und dann hier weiter wählst.
        </p>

        <h2 class="tc-group-title">Steuerungsfarbe</h2>
        <p class="tc-group-note">
          Die App trägt ihre Bereichsfarbe: Terrakotta — das AA-sichere Tiefen-Derivat des
          Pfirsich-Marks (die Marke selbst ist zu hell für Buttons und Links). Eigener Regler,
          getrennt von der Marke; kühle Alternativen bleiben wählbar.
        </p>
        <div class="tc-presets" role="group" aria-label="Steuerungsfarbe-Vorlagen">
          ${repeat(
            CONTROL_PRESETS,
            (p) => p.id,
            (p) => html`<button
              type="button"
              class="tc-preset"
              @click=${() => {
                setPresetPair("--control", p.light, p.dark)
                vm.bump()
              }}
            >
              <span
                class="tc-preset-swatch"
                style=${`background:${theme === "dark" ? p.dark : p.light}`}
                aria-hidden="true"
              ></span>
              ${p.label}
            </button>`,
          )}
        </div>
        <div class="tc-tokens">${repeat(controlToks, (t) => t.cssVar, (t) => this.tokenRow(vm, t))}</div>

        <h2 class="tc-group-title">Seitennavigation</h2>
        <p class="tc-group-note">
          Die linke Navigation hat ihren eigenen Akzent (Standard: das Chrome-Amber) — unabhängig
          von der Steuerungsfarbe im Inhalt.
        </p>
        <div class="tc-tokens">${repeat(navToks, (t) => t.cssVar, (t) => this.tokenRow(vm, t))}</div>

        <h2 class="tc-group-title">Semantische Zustände</h2>
        <p class="tc-group-note">
          Unabhängig von Marke und Steuerung. Teal bleibt ausschließlich Erfolg / Passung.
        </p>
        <div class="tc-tokens">${repeat(semanticToks, (t) => t.cssVar, (t) => this.tokenRow(vm, t))}</div>

        <h2 class="tc-group-title">Vorschau</h2>
        <div class="tc-preview">
          <button type="button" class="btn-primary">Primär-Button</button>
          <button type="button" class="btn-secondary">Sekundär</button>
          <a class="link" href="#" @click=${(e: Event) => e.preventDefault()}>Ein Link im Text</a>
          <a class="link-nav" href="#" @click=${(e: Event) => e.preventDefault()}>Zurück zur Suche</a>
          <a class="link-guide" href="#" @click=${(e: Event) => e.preventDefault()}>weiter mit Kompass</a>
          <a href="https://example.org" target="_blank" rel="noopener noreferrer">Externer Link</a>
          <span class="chip is-confirmed">Bestätigt</span>
          <span class="chip is-draft">Entwurf</span>
        </div>

        <h2 class="tc-group-title">Export &amp; Zurücksetzen</h2>
        <p class="tc-group-note">
          Fixierte Literale zum Einfügen in <code>index.html</code> (das theme-unabhängige Snapshot-Muster).
        </p>
        <textarea class="tc-export" readonly rows=${Math.min(14, Math.max(3, snippet.split("\n").length + 1))}>
${snippet || "/* Noch keine Overrides gesetzt — Standardwerte aktiv. */"}</textarea>
        <div class="tc-actions">
          <button type="button" class="btn-secondary" @click=${() => void vm.copyExport()}>
            ${vm.copied.value ? "Kopiert ✓" : "CSS kopieren"}
          </button>
          <button
            type="button"
            class="tc-reset"
            @click=${() => {
              clearAll()
              vm.bump()
            }}
          >
            Alles zurücksetzen
          </button>
        </div>
      </section>
    `
  }
}
